import { useEffect, useMemo, useState } from 'react';

import { useBankAccounts } from '../../../hooks/useBankAccounts';
import { useExchangeChecks } from '../../../hooks/useBankMovements';
import { useMovementConcepts } from '../../../hooks/useMovementConcepts';
import { useMovementTypes } from '../../../hooks/useMovementTypes';
import {
  useCreateSupplierPaymentOrder,
  useFinanceCashAccounts,
} from '../../../hooks/useSupplierLedger';
import {
  paymentOrderBaseSections,
  paymentOrderBatchSection,
} from '../constants/supplierLedgerUiConstants';
import {
  amountToInput,
  buildPaymentOrderGroups,
  buildPaymentOrderProviderGroups,
  createCheckLine,
  createEmptyPaymentForm,
  createRetentionLine,
  getInvoicesItemClassifications,
} from '../utils/supplierLedgerHelpers';
import {
  formatAmount,
  formatCurrencyAmount,
  parseNumber,
  toArgentinaISODate,
  toISODate,
} from '../utils/supplierLedgerFormatters';

const useCreatePaymentOrderDialogController = ({
  invoices,
  onClose,
  open,
  providerMap,
}) => {
  const [form, setForm] = useState({
    emission_date: toISODate(),
    observations: '',
  });
  const [applications, setApplications] = useState({});
  const [includedInvoices, setIncludedInvoices] = useState({});
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('summary');
  const [activeProviderId, setActiveProviderId] = useState('');
  const [providerStatuses, setProviderStatuses] = useState({});
  const [paymentForms, setPaymentForms] = useState({});
  // Clasificacion elegida para las OP cuyas facturas tienen importes con clasificaciones distintas (por grupo).
  const [orderClassifications, setOrderClassifications] = useState({});

  const createOrder = useCreateSupplierPaymentOrder();
  const { data: bankAccounts = [] } = useBankAccounts();
  const { data: cashAccounts = [] } = useFinanceCashAccounts();
  const { data: movementTypes = [] } = useMovementTypes();
  const { data: movementConcepts = [] } = useMovementConcepts();
  // Los cheques vencidos no se pueden usar: alcanza con traer los que vencen desde hoy.
  const exchangeCheckFilters = useMemo(() => ({
    start_date: toArgentinaISODate(),
    end_date: '2100-12-31',
  }), []);
  const { data: exchangeChecks = [] } = useExchangeChecks(exchangeCheckFilters);

  const exchangeCheckMap = useMemo(
    () => new Map(exchangeChecks.map((check) => [check.uuid, check])),
    [exchangeChecks],
  );

  const exchangeCheckOptions = useMemo(
    () => exchangeChecks.map((check) => {
      const dueDate = check.due_date ? String(check.due_date).slice(0, 10) : '';
      const amount = Math.abs(Number(check.amount || 0));
      const statusSuffix = check.is_expired
        ? ' · vencido'
        : check.is_used
          ? ' · usado'
          : '';
      return {
        ...check,
        value: check.uuid,
        label: `${check.voucher_number || check.check_number || 'Sin número'} · ${dueDate || 'sin venc.'} · ${formatCurrencyAmount(amount, check.currency || 'ARS')}${statusSuffix}`,
        disabled: check.is_expired || check.is_used || check.is_available === false,
      };
    }),
    [exchangeChecks],
  );

  const fundAccounts = useMemo(() => [
    ...bankAccounts.map((account) => ({
      ...account,
      fund_type: 'bank',
      label: `${account.bank_name || account.name || 'Banco'} ${account.account_number || ''}`.trim(),
    })),
    ...cashAccounts.map((account) => ({ ...account, fund_type: 'cash', label: account.name || 'Caja' })),
  ], [bankAccounts, cashAccounts]);

  const orderPaymentConcept = useMemo(
    () => movementConcepts.find((concept) => concept.name?.toLowerCase() === 'orden de pago'),
    [movementConcepts],
  );
  const defaultOrderMovementType = useMemo(
    () => movementTypes.find((type) => type.display_in_orders) || movementTypes[0],
    [movementTypes],
  );

  const providerGroups = useMemo(
    () => buildPaymentOrderProviderGroups(invoices, providerMap),
    [invoices, providerMap],
  );
  const providerCount = providerGroups.length;
  const hasMultipleProviders = providerCount > 1;
  const includedSelectedInvoices = useMemo(
    () => invoices.filter((invoice) => includedInvoices[invoice.uuid]),
    [includedInvoices, invoices],
  );
  const orderGroups = useMemo(
    () => buildPaymentOrderGroups(includedSelectedInvoices, providerMap),
    [includedSelectedInvoices, providerMap],
  );
  const providerWorkItems = useMemo(
    () => providerGroups
      .map((providerGroup) => {
        const included = providerGroup.invoices.filter((invoice) => includedInvoices[invoice.uuid]);
        return {
          provider: providerGroup,
          orderGroups: buildPaymentOrderGroups(included, providerMap),
        };
      })
      .filter((item) => item.orderGroups.length > 0),
    [includedInvoices, providerGroups, providerMap],
  );
  const paymentOrderSections = useMemo(
    () => (hasMultipleProviders
      ? [paymentOrderBatchSection, ...paymentOrderBaseSections]
      : paymentOrderBaseSections),
    [hasMultipleProviders],
  );

  const activeProvider = providerGroups.find((group) => group.providerId === activeProviderId) || providerGroups[0];
  const activeProviderIncludedInvoices = useMemo(
    () => activeProvider?.invoices.filter((invoice) => includedInvoices[invoice.uuid]) || [],
    [activeProvider, includedInvoices],
  );
  const activeProviderOrderGroups = useMemo(
    () => buildPaymentOrderGroups(activeProviderIncludedInvoices, providerMap),
    [activeProviderIncludedInvoices, providerMap],
  );
  const activePaymentForm = paymentForms[activeProvider?.providerId] || createEmptyPaymentForm(form.emission_date);
  const setActivePaymentForm = (updater) => {
    if (!activeProvider?.providerId) return;
    setPaymentForms((prev) => {
      const current = prev[activeProvider.providerId] || createEmptyPaymentForm(form.emission_date);
      const next = typeof updater === 'function' ? updater(current) : updater;
      return { ...prev, [activeProvider.providerId]: next };
    });
  };

  useEffect(() => {
    if (!open) return;
    setApplications(
      invoices.reduce((acc, invoice) => {
        acc[invoice.uuid] = String(invoice.pending_amount || 0);
        return acc;
      }, {}),
    );
    setIncludedInvoices(
      invoices.reduce((acc, invoice) => {
        acc[invoice.uuid] = true;
        return acc;
      }, {}),
    );
    setError('');
    setActiveSection(hasMultipleProviders ? 'batch' : 'summary');
    setActiveProviderId(providerGroups[0]?.providerId || '');
    setProviderStatuses({});
    setOrderClassifications({});
    setPaymentForms(
      providerGroups.reduce((acc, group) => {
        acc[group.providerId] = createEmptyPaymentForm(toISODate());
        return acc;
      }, {}),
    );
  }, [hasMultipleProviders, invoices, open, providerGroups]);

  const total = useMemo(
    () => includedSelectedInvoices.reduce((sum, invoice) => sum + parseNumber(applications[invoice.uuid] || 0), 0),
    [applications, includedSelectedInvoices],
  );
  const totalArs = useMemo(
    () => includedSelectedInvoices.reduce((sum, invoice) => {
      const applied = parseNumber(applications[invoice.uuid] || 0);
      const pending = Number(invoice.pending_amount || 0);
      const pendingArs = Number(invoice.pending_amount_ars || 0);
      const rate = pending > 0 ? pendingArs / pending : 1;
      return sum + (applied * rate);
    }, 0),
    [applications, includedSelectedInvoices],
  );
  const activeProviderTotal = useMemo(
    () => activeProviderIncludedInvoices.reduce((sum, invoice) => sum + parseNumber(applications[invoice.uuid] || 0), 0),
    [activeProviderIncludedInvoices, applications],
  );
  const activeProviderTotalArs = useMemo(
    () => activeProviderIncludedInvoices.reduce((sum, invoice) => {
      const applied = parseNumber(applications[invoice.uuid] || 0);
      const pending = Number(invoice.pending_amount || 0);
      const pendingArs = Number(invoice.pending_amount_ars || 0);
      const rate = pending > 0 ? pendingArs / pending : 1;
      return sum + (applied * rate);
    }, 0),
    [activeProviderIncludedInvoices, applications],
  );

  const singleGroup = orderGroups.length === 1 ? orderGroups[0] : null;
  const activeRetentionsTotal = useMemo(
    () => (activePaymentForm.retentions || []).reduce((sum, row) => sum + parseNumber(row.amount || 0), 0),
    [activePaymentForm.retentions],
  );
  const activeChecksTotal = useMemo(
    () => (activePaymentForm.checks || []).reduce((sum, row) => sum + parseNumber(row.amount || 0), 0),
    [activePaymentForm.checks],
  );
  const activeNetPayable = Math.max(activeProviderTotal - activeRetentionsTotal, 0);
  const activePaymentCanBeRegistered = activeProviderOrderGroups.length === 1;
  const footerTotalLabel = hasMultipleProviders || orderGroups.length > 1 ? 'Total lote ARS' : 'Total de la orden';
  const footerTotalValue = singleGroup
    ? formatCurrencyAmount(total, singleGroup.currency)
    : formatAmount(totalArs);
  const allProvidersDone = providerWorkItems.length > 0
    && providerWorkItems.every((item) => providerStatuses[item.provider.providerId] === 'done');

  // Una OP lleva una sola clasificacion: la comun a todos los importes de sus facturas o, si difieren, la que se elija.
  const getGroupClassification = (group) => {
    const classifications = getInvoicesItemClassifications(group.invoices);
    if (classifications.length > 1) {
      return {
        isMixed: true,
        classification: orderClassifications[group.id] || { cost_behavior: '', cost_nature: '', cost_area: '' },
      };
    }
    return { isMixed: false, classification: classifications[0] || {} };
  };
  const isClassificationComplete = (classification) => Boolean(
    classification.cost_behavior && classification.cost_nature && classification.cost_area,
  );
  const activeProviderMixedClassificationGroups = activeProviderOrderGroups
    .map((group) => ({ group, ...getGroupClassification(group) }))
    .filter((item) => item.isMixed);

  const handleOrderClassificationChange = (groupId, field, value) => {
    setOrderClassifications((prev) => ({
      ...prev,
      [groupId]: {
        cost_behavior: '',
        cost_nature: '',
        cost_area: '',
        ...prev[groupId],
        [field]: value,
      },
    }));
  };

  const hasInvalidApplications = includedSelectedInvoices.some((invoice) => {
    const amount = parseNumber(applications[invoice.uuid] || 0);
    const pending = Number(invoice.pending_amount || 0);
    return amount <= 0 || amount > pending;
  });

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === 'emission_date') {
      setPaymentForms((prev) => Object.fromEntries(
        Object.entries(prev).map(([providerId, paymentForm]) => [
          providerId,
          { ...paymentForm, due_date: paymentForm.due_date || value },
        ]),
      ));
    }
  };

  const handlePaymentChange = (event) => {
    const { name, value, checked, type } = event.target;
    setActivePaymentForm((prev) => {
      const next = { ...prev, [name]: type === 'checkbox' ? checked : value };
      if (name === 'fund_account_id') {
        const account = fundAccounts.find((item) => item.uuid === value);
        next.uuid_bank_account = account?.fund_type === 'bank' ? value : '';
        next.uuid_cash_account = account?.fund_type === 'cash' ? value : '';
      }
      if (name === 'payment_method' && value === 'cash') {
        const cash = fundAccounts.find((item) => item.fund_type === 'cash');
        next.fund_account_id = cash?.uuid || '';
        next.uuid_cash_account = cash?.uuid || '';
        next.uuid_bank_account = '';
      }
      if (name === 'payment_method' && value !== 'cash' && next.uuid_cash_account) {
        next.fund_account_id = '';
        next.uuid_cash_account = '';
      }
      return next;
    });
  };

  const updatePaymentCollection = (collection, updater) => {
    setActivePaymentForm((prev) => ({
      ...prev,
      [collection]: typeof updater === 'function' ? updater(prev[collection] || []) : updater,
    }));
  };

  const addRetention = () => updatePaymentCollection('retentions', (rows) => [...rows, createRetentionLine()]);
  const addCheck = () => updatePaymentCollection('checks', (rows) => [...rows, createCheckLine(activeProviderOrderGroups[0]?.currency || 'ARS', form.emission_date)]);
  const updateRetention = (id, field, value) => updatePaymentCollection('retentions', (rows) => rows.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
  const updateCheck = (id, field, value) => updatePaymentCollection('checks', (rows) => rows.map((row) => {
    if (row.id !== id) return row;

    const next = { ...row, [field]: value };
    if (field === 'check_kind') {
      if (value === 'third_party') {
        next.uuid_bank_account = '';
      } else {
        next.uuid_exchange_check_movement = '';
      }
    }

    if (field === 'uuid_exchange_check_movement') {
      const check = exchangeCheckMap.get(value);
      if (check) {
        next.check_number = check.voucher_number || check.check_number || '';
        next.emission_date = check.emission_date ? String(check.emission_date).slice(0, 10) : form.emission_date;
        next.due_date = check.due_date ? String(check.due_date).slice(0, 10) : form.emission_date;
        next.currency = check.currency || activeProviderOrderGroups[0]?.currency || 'ARS';
        next.exchange_rate_to_ars = check.exchange_rate_to_ars || '';
        // amountToInput: formato es-AR sin simbolo de moneda, que es lo que parseNumber sabe leer.
        next.amount = amountToInput(Math.abs(Number(check.amount || 0)));
      }
    }

    return next;
  }));
  const removeRetention = (id) => updatePaymentCollection('retentions', (rows) => rows.filter((row) => row.id !== id));
  const removeCheck = (id) => updatePaymentCollection('checks', (rows) => rows.filter((row) => row.id !== id));

  const handleApplicationChange = (invoiceId, value) => {
    setApplications((prev) => ({ ...prev, [invoiceId]: value }));
  };

  const handleToggleInvoice = (invoiceId, checked) => {
    setIncludedInvoices((prev) => ({ ...prev, [invoiceId]: checked }));
  };

  const handleToggleProvider = (providerId, checked) => {
    const providerGroup = providerGroups.find((group) => group.providerId === providerId);
    if (!providerGroup) return;

    setIncludedInvoices((prev) => {
      const next = { ...prev };
      providerGroup.invoices.forEach((invoice) => {
        next[invoice.uuid] = checked;
      });
      return next;
    });
    setActiveProviderId(providerId);
  };

  const workProvider = (providerId = activeProvider?.providerId, section = 'summary') => {
    if (!providerId) return;
    setActiveProviderId(providerId);
    setActiveSection(section);
  };

  const markReadyAndMoveNext = () => {
    if (!activeProvider) return;
    setProviderStatuses((prev) => ({ ...prev, [activeProvider.providerId]: 'ready' }));
    const currentIndex = providerGroups.findIndex((group) => group.providerId === activeProvider.providerId);
    const nextProvider = providerGroups.slice(currentIndex + 1).find((group) => (
      group.invoices.some((invoice) => includedInvoices[invoice.uuid])
    ));
    if (nextProvider) {
      setActiveProviderId(nextProvider.providerId);
    }
    setActiveSection('batch');
  };

  const buildPayloadForGroup = (group) => {
    const { classification } = getGroupClassification(group);
    const paymentForm = paymentForms[group.providerId] || createEmptyPaymentForm(form.emission_date);
    const retentions = paymentForm.pay_now
      ? (paymentForm.retentions || [])
        .filter((row) => parseNumber(row.amount) > 0)
        .map((row) => ({
          retention_type: row.retention_type,
          certificate_number: row.certificate_number || null,
          amount: parseNumber(row.amount),
          observations: row.observations || null,
        }))
      : [];
    const checks = paymentForm.pay_now
      ? (paymentForm.checks || [])
        .filter((row) => parseNumber(row.amount) > 0)
        .map((row) => ({
          check_kind: row.check_kind,
          uuid_bank_account: row.check_kind === 'own' ? row.uuid_bank_account || null : null,
          uuid_exchange_check_movement: row.check_kind === 'third_party' ? row.uuid_exchange_check_movement || null : null,
          check_number: row.check_number,
          emission_date: row.emission_date || form.emission_date,
          due_date: row.due_date,
          currency: row.currency || group.currency,
          exchange_rate_to_ars: row.currency === 'USD' ? parseNumber(row.exchange_rate_to_ars) : null,
          amount: parseNumber(row.amount),
        }))
      : [];
    const appliedTotal = group.invoices.reduce((sum, invoice) => sum + parseNumber(applications[invoice.uuid] || 0), 0);
    const retentionsTotal = retentions.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const checksTotal = checks.reduce((sum, row) => sum + Number(row.amount || 0), 0);
    const movementAmount = Math.max(appliedTotal - retentionsTotal - checksTotal, 0);
    const shouldCreateMovement = paymentForm.pay_now
      && ['transfer', 'cash'].includes(paymentForm.payment_method)
      && movementAmount > 0;

    return {
      uuid_legal_entity: group.providerId,
      emission_date: new Date(form.emission_date).toISOString(),
      observations: form.observations || null,
      status: paymentForm.pay_now ? 'paid' : 'pending',
      payment_method: paymentForm.pay_now ? paymentForm.payment_method : null,
      uuid_cash_account: paymentForm.pay_now ? paymentForm.uuid_cash_account || null : null,
      currency: group.currency,
      exchange_rate_to_ars: group.currency === 'USD' ? group.exchangeRateToArs : null,
      cost_behavior: classification.cost_behavior || null,
      cost_nature: classification.cost_nature || null,
      cost_traceability: classification.cost_nature || null,
      cost_area: classification.cost_area || null,
      invoice_applications: group.invoices.map((invoice) => ({
        uuid_supplier_invoice: invoice.uuid,
        applied_amount: parseNumber(applications[invoice.uuid] || 0),
      })),
      bank_movements: shouldCreateMovement ? [{
        uuid_bank_account: paymentForm.uuid_bank_account || null,
        uuid_cash_account: paymentForm.uuid_cash_account || null,
        uuid_movement_type: paymentForm.uuid_movement_type || defaultOrderMovementType?.uuid || null,
        emission_date: new Date(form.emission_date).toISOString(),
        due_date: new Date(paymentForm.due_date || form.emission_date).toISOString(),
        amount: -movementAmount,
        concept: 'Orden de Pago',
        uuid_movement_concept: orderPaymentConcept?.uuid || null,
        voucher_number: paymentForm.voucher_number || null,
        display_in_book: true,
      }] : [],
      retentions,
      checks,
    };
  };

  const submit = async () => {
    setError('');
    if (!includedSelectedInvoices.length) {
      setError('Tenés que incluir al menos una factura para generar OP.');
      setActiveSection(hasMultipleProviders ? 'batch' : 'invoices');
      return;
    }
    if (hasInvalidApplications) {
      setError('Cada importe a pagar debe ser mayor a cero y no superar el pendiente de la factura.');
      const invalidInvoice = includedSelectedInvoices.find((invoice) => {
        const amount = parseNumber(applications[invoice.uuid] || 0);
        const pending = Number(invoice.pending_amount || 0);
        return amount <= 0 || amount > pending;
      });
      if (invalidInvoice) {
        setActiveProviderId(invalidInvoice.uuid_legal_entity);
      }
      setActiveSection('invoices');
      return;
    }

    const itemWithoutClassification = providerWorkItems.find((item) => item.orderGroups.some((group) => {
      const { isMixed, classification } = getGroupClassification(group);
      return isMixed && !isClassificationComplete(classification);
    }));
    if (itemWithoutClassification) {
      setActiveProviderId(itemWithoutClassification.provider.providerId);
      setActiveSection('data');
      setError('Las facturas incluidas tienen importes con clasificaciones distintas: elegí la clasificación de la orden de pago.');
      return;
    }

    const invalidPaymentItem = providerWorkItems.find((item) => {
      const paymentForm = paymentForms[item.provider.providerId];
      if (!paymentForm?.pay_now) return false;
      if (item.orderGroups.length !== 1) return true;
      const group = item.orderGroups[0];
      const appliedTotal = group.invoices.reduce((sum, invoice) => sum + parseNumber(applications[invoice.uuid] || 0), 0);
      const retentionsTotal = (paymentForm.retentions || []).reduce((sum, row) => sum + parseNumber(row.amount || 0), 0);
      const checksTotal = (paymentForm.checks || []).reduce((sum, row) => sum + parseNumber(row.amount || 0), 0);
      const netPayable = appliedTotal - retentionsTotal;
      if (netPayable < 0) return true;
      if (['transfer', 'cash'].includes(paymentForm.payment_method)) {
        if (!paymentForm.fund_account_id || !(paymentForm.uuid_movement_type || defaultOrderMovementType?.uuid) || !paymentForm.voucher_number || !paymentForm.due_date) return true;
        return checksTotal > netPayable;
      }
      if (!paymentForm.checks?.length) return true;
      if (Math.abs(checksTotal - netPayable) >= 0.01) return true;
      return paymentForm.checks.some((check) => (
        (check.check_kind === 'own' && !check.check_number)
        || !check.due_date
        || parseNumber(check.amount) <= 0
        || (check.check_kind === 'own' && !check.uuid_bank_account)
        || (check.check_kind === 'third_party' && (
          !check.uuid_exchange_check_movement
          || exchangeCheckMap.get(check.uuid_exchange_check_movement)?.is_expired
          || exchangeCheckMap.get(check.uuid_exchange_check_movement)?.is_used
          || exchangeCheckMap.get(check.uuid_exchange_check_movement)?.is_available === false
        ))
      ));
    });
    if (invalidPaymentItem) {
      setActiveProviderId(invalidPaymentItem.provider.providerId);
      setActiveSection('payment');
      setError('Revisá los medios de pago: para registrar pago inmediato debe haber una sola OP por proveedor, medio completo y neto cubierto.');
      return;
    }
    try {
      if (hasMultipleProviders) {
        setActiveSection('batch');
      }

      for (const item of providerWorkItems) {
        setActiveProviderId(item.provider.providerId);
        setProviderStatuses((prev) => ({ ...prev, [item.provider.providerId]: 'processing' }));
        try {
          for (const group of item.orderGroups) {
            await createOrder.mutateAsync(buildPayloadForGroup(group));
          }
        } catch (err) {
          setProviderStatuses((prev) => ({ ...prev, [item.provider.providerId]: 'error' }));
          throw err;
        }
        setProviderStatuses((prev) => ({ ...prev, [item.provider.providerId]: 'done' }));
      }

      if (hasMultipleProviders) {
        setActiveSection('batch');
        return;
      }

      onClose();
    } catch (err) {
      setError(err?.response?.data?.error || err?.response?.data?.message || err.message);
    }
  };

  return {
    activeChecksTotal,
    activeNetPayable,
    activePaymentCanBeRegistered,
    activePaymentForm,
    activeProvider,
    activeProviderId,
    activeProviderIncludedInvoices,
    activeProviderMixedClassificationGroups,
    activeProviderOrderGroups,
    activeProviderTotal,
    activeProviderTotalArs,
    activeRetentionsTotal,
    activeSection,
    addCheck,
    addRetention,
    allProvidersDone,
    applications,
    bankAccounts,
    createOrder,
    error,
    footerTotalLabel,
    footerTotalValue,
    form,
    fundAccounts,
    exchangeCheckOptions,
    handleApplicationChange,
    handleChange,
    handleOrderClassificationChange,
    handlePaymentChange,
    handleToggleInvoice,
    handleToggleProvider,
    hasInvalidApplications,
    hasMultipleProviders,
    includedInvoices,
    includedSelectedInvoices,
    invoices,
    markReadyAndMoveNext,
    movementTypes,
    onClose,
    paymentForms,
    paymentOrderSections,
    providerCount,
    providerGroups,
    providerMap,
    providerStatuses,
    providerWorkItems,
    removeCheck,
    removeRetention,
    setActiveProviderId,
    setActiveSection,
    submit,
    total,
    totalArs,
    updateCheck,
    updateRetention,
    workProvider,
  };
};

export default useCreatePaymentOrderDialogController;
